import { ColumnDef } from '@tanstack/react-table';

import { Grid } from '@/components/core/Grid';
import { useTranslation } from '@/hooks/useTranslation';

export interface EventData {
    [key: string]: any;
    primaryKey: string;
    event: string;
    date: number;
    entityType: string;
    entityId: string;
    status: string;
    assetType: string;
    assetId: string;
    assetPath: string;
    additionalText: string;
    user?: string;
}

interface GpcData {
    oldValue__: string | null;
    currentValue__: string | null;
}

export interface AttributeData {
    updatedAt__: number;
    updatedBy__: string;
    checksum__: string;
    gpc: GpcData;
}

interface HistoryGridProps {
    data: EventData[];
    columns: ColumnDef<any>[];
    gridKey: string
}

type IndexedEventRow = { row: EventData; index: number };

function eventHistoryGroupKey({ row, index }: IndexedEventRow): string {
    const raw = row.entityId;
    if (raw != null && String(raw) !== '') {
        const type = row.entityType != null ? String(row.entityType) : '';
        return `e:${type}:${String(raw)}`;
    }
    return `s:${index}`;
}

export function sortEventHistoryForDisplay(data: EventData[]): EventData[] {
    const indexed: IndexedEventRow[] = data.map((row, index) => ({ row, index }));
    const maxByGroup = new Map<string, number>();
    for (const item of indexed) {
        const k = eventHistoryGroupKey(item);
        const d = Number(item.row.date) || 0;
        maxByGroup.set(k, Math.max(maxByGroup.get(k) ?? d, d));
    }
    return indexed
        .sort((a, b) => {
            const ka = eventHistoryGroupKey(a);
            const kb = eventHistoryGroupKey(b);
            const maxA = maxByGroup.get(ka) ?? 0;
            const maxB = maxByGroup.get(kb) ?? 0;
            if (maxB !== maxA) return maxB - maxA;
            if (ka !== kb) return a.index - b.index;
            return (Number(b.row.date) || 0) - (Number(a.row.date) || 0);
        })
        .map(({ row }) => row);
}

const ItemHistoryGrid = ({ data, columns, gridKey }: HistoryGridProps) => {
    const { t } = useTranslation();

    const formattedData = sortEventHistoryForDisplay([...data]).map((item) => ({
        ...item,
        event: t(item.event),
    }));

    return (
        <div className="!tw-h-full !tw-min-h-[70vh] tw-flex tw-flex-col">
            <Grid
                burgerMenu={true}
                isWrapContent={true}
                storedStateKey={gridKey}
                insideItemEditor={true}
                columns={columns}
                data={formattedData}
                enableSorting={true}
                className='tw-basis-[35rem] !tw-h-full !tw-min-h-[70vh]' />
        </div>
    );
};

export default ItemHistoryGrid;
